import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import tempMonitoringService from '../../services/tempMonitoringService';
import SqfNav from '../../components/SqfNav';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, TimeScale, Filler);

// ── Unit helpers ──────────────────────────────────────────────────────────────
const cToF   = c => c != null ? (c * 9 / 5 + 32) : null;
const fmtTemp = (tempC, unit) => {
  if (tempC == null) return '—';
  return unit === 'F'
    ? `${cToF(tempC).toFixed(1)} °F`
    : `${tempC.toFixed(1)} °C`;
};

// ── Status helpers ────────────────────────────────────────────────────────────
function sensorStatus(sensor) {
  // Equipment flagged under_maintenance/out_of_service in Maintenance has its alarms suppressed
  // (services/tempMonitoring/assetSuppression.js) — surface that here too so the card itself
  // reads as "don't worry about this one" instead of showing a stale/misleading in-range badge.
  if (sensor.maintenanceAsset && ['under_maintenance', 'out_of_service'].includes(sensor.maintenanceAsset.status)) {
    return {
      label: sensor.maintenanceAsset.status === 'under_maintenance' ? '🔧 Under Maintenance' : '🔧 Out of Service',
      color: 'warning',
      suppressed: true,
    };
  }
  if (sensor.lastTempC == null) return { label: 'No data', color: 'secondary' };
  const t = sensor.lastTempC;
  if ((sensor.minTempC != null && t < sensor.minTempC) ||
      (sensor.maxTempC != null && t > sensor.maxTempC)) {
    return { label: 'Out of range', color: 'danger' };
  }
  return { label: 'In range', color: 'success' };
}

function timeSince(dt) {
  if (!dt) return null;
  const mins = Math.round((Date.now() - new Date(dt)) / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Acknowledge modal ─────────────────────────────────────────────────────────
const COMMON_REASONS = [
  'Door held open during loading / unloading',
  'Defrost cycle',
  'Unit heavily loaded — air circulation reduced',
  'Equipment malfunction — service called',
  'Power outage / brief power interruption',
  'Sensor calibration check / maintenance',
  'Ambient temperature spike (delivery area)',
  'Other',
];

function AcknowledgeModal({ reading, onClose, onSaved }) {
  const [preset,     setPreset]  = useState('');
  const [customText, setCustom]  = useState('');
  const [saving,     setSaving]  = useState(false);
  const [err,        setErr]     = useState('');

  const isOther  = preset === 'Other';
  const finalReason = isOther ? customText.trim() : preset;

  async function save() {
    if (!finalReason) { setErr(isOther ? 'Please describe the reason.' : 'Please select a reason.'); return; }
    setSaving(true);
    try {
      await tempMonitoringService.acknowledgeReading(reading.id, finalReason);
      onSaved();
    } catch { setErr('Failed to save.'); setSaving(false); }
  }

  const sensor = reading.Sensor;
  const unit   = sensor?.displayUnit || 'F';

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Acknowledge Out-of-Range Reading</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 13 }}>
              <strong>{sensor?.name}</strong> — {fmtTemp(reading.tempC, unit)}<br />
              <span className="text-muted">{new Date(reading.recordedAt).toLocaleString()}</span>
            </div>
            {err && <div className="text-danger mb-2" style={{ fontSize: 13 }}>{err}</div>}
            <label className="form-label">Reason for out-of-range temperature <span className="text-danger">*</span></label>
            <select
              className="form-select mb-2"
              value={preset}
              onChange={e => { setPreset(e.target.value); setCustom(''); setErr(''); }}
              autoFocus
            >
              <option value="">— select a reason —</option>
              {COMMON_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {isOther && (
              <textarea
                className="form-control"
                rows={3}
                value={customText}
                onChange={e => { setCustom(e.target.value); setErr(''); }}
                placeholder="Describe the reason…"
                autoFocus
              />
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save & Acknowledge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Manual entry modal ────────────────────────────────────────────────────────
function ManualEntryModal({ sensors, onClose, onSaved }) {
  const [sensorId,   setSensorId]   = useState('');
  const [tempInput,  setTempInput]  = useState('');
  const [humidity,   setHumidity]   = useState('');
  const [recordedAt, setRecordedAt] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState('');

  const sensor = sensors.find(s => String(s.id) === String(sensorId));
  const unit   = sensor?.displayUnit || 'F';

  const nowLocal = () => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  useEffect(() => { setRecordedAt(nowLocal()); }, []);

  async function save() {
    if (!sensorId || tempInput === '') { setErr('Select a sensor and enter a temperature.'); return; }
    setSaving(true);
    try {
      const tempC = unit === 'F'
        ? (Number(tempInput) - 32) * 5 / 9
        : Number(tempInput);
      await tempMonitoringService.createReading({
        sensorId: Number(sensorId),
        tempC,
        humidity: humidity !== '' ? Number(humidity) : null,
        recordedAt: recordedAt ? new Date(recordedAt).toISOString() : null,
      });
      onSaved();
    } catch { setErr('Failed to save reading.'); setSaving(false); }
  }

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Manual Temperature Entry</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {err && <div className="text-danger mb-2" style={{ fontSize: 13 }}>{err}</div>}
            <div className="mb-3">
              <label className="form-label">Sensor <span className="text-danger">*</span></label>
              <select className="form-select" value={sensorId} onChange={e => setSensorId(e.target.value)}>
                <option value="">— select sensor —</option>
                {sensors.filter(s => s.isActive).map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.locationName ? ` — ${s.locationName}` : ''}</option>
                ))}
              </select>
            </div>
            <div className="row g-2 mb-3">
              <div className="col">
                <label className="form-label">Temperature ({unit === 'F' ? '°F' : '°C'}) <span className="text-danger">*</span></label>
                <input type="number" step="0.1" className="form-control" value={tempInput}
                  onChange={e => { setTempInput(e.target.value); setErr(''); }} placeholder={unit === 'F' ? '38' : '3.3'} />
              </div>
              <div className="col">
                <label className="form-label">Humidity (%)</label>
                <input type="number" step="1" min="0" max="100" className="form-control" value={humidity}
                  onChange={e => setHumidity(e.target.value)} placeholder="optional" />
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label">Date &amp; Time</label>
              <input type="datetime-local" className="form-control" value={recordedAt}
                onChange={e => setRecordedAt(e.target.value)} />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Reading'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sensor chart modal ────────────────────────────────────────────────────────
const PERIODS = [
  { key: '24h', label: '24 h' },
  { key: '7d',  label: '7 days' },
  { key: '30d', label: '30 days' },
];

function SensorChartModal({ sensor, onClose }) {
  const [period,   setPeriod]   = useState('24h');
  const [chartData, setChartData] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [err,      setErr]      = useState('');

  useEffect(() => {
    setLoading(true);
    setErr('');
    tempMonitoringService.getSensorChart(sensor.id, period)
      .then(d => setChartData(d))
      .catch(() => setErr('Failed to load chart data.'))
      .finally(() => setLoading(false));
  }, [sensor.id, period]);

  const unit = sensor.displayUnit || 'F';

  const { data, options } = useMemo(() => {
    if (!chartData?.readings?.length) return { data: null, options: null };

    const readings = chartData.readings;
    const toDisplay = c => unit === 'F' ? cToF(c) : c;

    const mainPoints = readings.map(r => ({
      x: new Date(r.recordedAt),
      y: toDisplay(r.tempC),
      oor: r.isOutOfRange,
    }));

    const pointColors = mainPoints.map(p =>
      p.oor ? 'rgba(220,53,69,0.85)' : 'rgba(25,135,84,0.75)'
    );

    const datasets = [
      {
        label: `Temperature (°${unit})`,
        data: mainPoints,
        borderColor: '#1b2638',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: mainPoints.length > 150 ? 1 : 3,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        tension: 0.15,
        order: 1,
      },
    ];

    if (sensor.minTempC != null) {
      const minVal = toDisplay(sensor.minTempC);
      datasets.push({
        label: `Min (${fmtTemp(sensor.minTempC, unit)})`,
        data: mainPoints.map(p => ({ x: p.x, y: minVal })),
        borderColor: 'rgba(220,53,69,0.5)',
        borderDash: [6, 3],
        borderWidth: 1.5,
        pointRadius: 0,
        backgroundColor: 'transparent',
        order: 2,
      });
    }

    if (sensor.maxTempC != null) {
      const maxVal = toDisplay(sensor.maxTempC);
      datasets.push({
        label: `Max (${fmtTemp(sensor.maxTempC, unit)})`,
        data: mainPoints.map(p => ({ x: p.x, y: maxVal })),
        borderColor: 'rgba(220,53,69,0.5)',
        borderDash: [6, 3],
        borderWidth: 1.5,
        pointRadius: 0,
        backgroundColor: 'transparent',
        order: 2,
      });
    }

    if (readings.some(r => r.humidity != null)) {
      datasets.push({
        label: 'Humidity (%)',
        data: readings
          .filter(r => r.humidity != null)
          .map(r => ({ x: new Date(r.recordedAt), y: r.humidity })),
        borderColor: 'rgba(13,110,253,0.55)',
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0.15,
        yAxisID: 'y2',
        order: 3,
      });
    }

    const hasHumidity = datasets.some(d => d.yAxisID === 'y2');

    const timeUnit = period === '30d' ? 'day' : period === '7d' ? 'day' : 'hour';
    const tooltipFmt = period === '30d' ? 'MMM d' : period === '7d' ? 'MMM d HH:mm' : 'HH:mm';
    const displayFmts = period === '30d'
      ? { day: 'MMM d' }
      : period === '7d'
      ? { day: 'MMM d', hour: 'HH:mm' }
      : { hour: 'HH:mm' };

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
        tooltip: {
          callbacks: {
            label: ctx => {
              if (ctx.dataset.yAxisID === 'y2') return ` ${ctx.parsed.y.toFixed(0)}% RH`;
              return ` ${ctx.parsed.y.toFixed(1)} °${unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          time: { unit: timeUnit, tooltipFormat: tooltipFmt, displayFormats: displayFmts },
          ticks: { font: { size: 10 }, maxTicksLimit: 10 },
        },
        y: {
          title: { display: true, text: `°${unit}`, font: { size: 11 } },
          ticks: { font: { size: 11 } },
        },
        ...(hasHumidity ? {
          y2: {
            position: 'right',
            title: { display: true, text: '% RH', font: { size: 11 } },
            ticks: { font: { size: 10 } },
            grid: { drawOnChartArea: false },
            min: 0,
            max: 100,
          },
        } : {}),
      },
    };

    return { data: { datasets }, options: opts };
  }, [chartData, sensor, unit, period]);

  const st = sensorStatus(sensor);

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header py-2 px-3">
            <div>
              <h5 className="modal-title mb-0">{sensor.name}</h5>
              {sensor.locationName && <small className="text-muted">{sensor.locationName}</small>}
            </div>
            <div className="d-flex align-items-center gap-3 ms-auto me-3">
              <span className={`badge bg-${st.color}`}>{st.label}</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: st.color === 'danger' ? '#dc3545' : st.color === 'success' ? '#198754' : '#6c757d' }}>
                {fmtTemp(sensor.lastTempC, sensor.displayUnit || 'F')}
              </span>
            </div>
            <button className="btn-close" onClick={onClose} />
          </div>

          {/* Period tabs */}
          <div className="border-bottom px-3 pt-2 pb-0">
            <ul className="nav nav-tabs border-0 gap-1">
              {PERIODS.map(p => (
                <li key={p.key} className="nav-item">
                  <button
                    className={`nav-link py-1 px-3${period === p.key ? ' active' : ''}`}
                    style={{ fontSize: 13 }}
                    onClick={() => setPeriod(p.key)}
                  >{p.label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div className="modal-body" style={{ minHeight: 320 }}>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                <div className="spinner-border spinner-border-sm text-secondary" />
              </div>
            ) : err ? (
              <div className="alert alert-danger py-2">{err}</div>
            ) : !data ? (
              <div className="text-center text-muted py-5">No readings in this period.</div>
            ) : (
              <div style={{ height: 320 }}>
                <Line data={data} options={options} />
              </div>
            )}
            {chartData?.readings?.length > 0 && (
              <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                {chartData.readings.length} readings · Red points = out of range · Dashed lines = configured thresholds
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TempMonitoringPage() {
  const [sensors,      setSensors]      = useState([]);
  const [readingsData, setReadingsData] = useState({ readings: [], total: 0, pages: 1 });
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');

  // Filters
  const [filterSensor,  setFilterSensor]  = useState('');
  const [filterStart,   setFilterStart]   = useState('');
  const [filterEnd,     setFilterEnd]     = useState('');
  const [filterFlagged, setFilterFlagged] = useState(false);
  const [page,          setPage]          = useState(1);

  // Modals
  const [ackReading,   setAckReading]   = useState(null);
  const [showManual,   setShowManual]   = useState(false);
  const [chartSensor,  setChartSensor]  = useState(null);

  // Sensor peek bar (visible when sensor cards scroll out of view)
  const sensorsRef      = useRef(null);
  const [sensorsHidden, setSensorsHidden] = useState(false);
  const [peekOpen,      setPeekOpen]      = useState(false);

  useEffect(() => {
    const el = sensorsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        setSensorsHidden(!entry.isIntersecting);
        if (entry.isIntersecting) setPeekOpen(false);
      },
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const load = useCallback(async (background = false) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    try {
      const params = { page };
      if (filterSensor)        params.sensorId    = filterSensor;
      if (filterStart)         params.startDate   = filterStart;
      if (filterEnd)           params.endDate     = filterEnd;
      if (filterFlagged)       params.flaggedOnly = 'true';

      const [s, r] = await Promise.all([
        tempMonitoringService.getSensors(),
        tempMonitoringService.getReadings(params),
      ]);
      setSensors(s);
      setReadingsData(r);
    } catch { setError('Failed to load data.'); }
    finally { setLoading(false); setRefreshing(false); }
  }, [page, filterSensor, filterStart, filterEnd, filterFlagged]);

  useEffect(() => { load(); }, [load]);

  function flash(msg) { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); }

  const activeSensors = sensors.filter(s => s.isActive);

  const unflaggedReadings = readingsData.readings.filter(r => r.isFlagged && !r.acknowledgedAt);

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h4 className="mb-0">Temperature Monitoring</h4>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowManual(true)}>+ Manual Entry</button>
          <Link to="/sqf/temp-monitoring/config" className="btn btn-outline-secondary btn-sm">⚙ Config</Link>
          <Link to="/sqf" className="btn btn-outline-secondary btn-sm">← SQF Dashboard</Link>
        </div>
      </div>

      {error   && <div className="alert alert-danger  py-2">{error}   <button className="btn-close float-end btn-sm" onClick={() => setError('')} /></div>}
      {success && <div className="alert alert-success py-2">{success}</div>}

      {/* ── Flagged alert banner ──────────────────────────────────────────── */}
      {unflaggedReadings.length > 0 && (
        <div className="alert alert-danger py-2 mb-3 d-flex align-items-center gap-3">
          <span>⚠ <strong>{unflaggedReadings.length}</strong> unacknowledged flagged reading(s) require attention.</span>
          <button className="btn btn-danger btn-sm ms-auto" onClick={() => { setFilterFlagged(true); setPage(1); }}>
            Review Flags
          </button>
        </div>
      )}

      {/* ── Sensor cards ─────────────────────────────────────────────────── */}
      {activeSensors.length === 0 && !loading ? (
        <div ref={sensorsRef} className="text-center text-muted py-4 border rounded mb-4">
          No sensors configured. <Link to="/sqf/temp-monitoring/config">Add sensors →</Link>
        </div>
      ) : (
        <div ref={sensorsRef} className="row g-3 mb-4">
          {activeSensors.map(s => {
            const st   = sensorStatus(s);
            const unit = s.displayUnit || 'F';
            return (
              <div key={s.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                <div
                  className={`card h-100 shadow-sm ${st.suppressed ? 'border border-warning border-2' : `border-${st.color} border-start border-start-4`}`}
                  style={{ cursor: 'pointer', ...(st.suppressed ? { background: '#fff9e6' } : {}) }}
                  onClick={() => setChartSensor(s)}
                  title="Click to view temperature chart"
                >
                  <div className="card-body py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="fw-semibold" style={{ fontSize: 13 }}>{s.name}</div>
                      <div className="d-flex gap-1 align-items-center">
                        <span className="text-muted" style={{ fontSize: 10 }}>📈</span>
                        <span className={`badge bg-${st.color}`} style={{ fontSize: 10 }}>{st.label}</span>
                      </div>
                    </div>
                    {s.locationName && <div className="text-muted" style={{ fontSize: 11 }}>{s.locationName}</div>}
                    {st.suppressed && (
                      <Link to={`/maintenance/assets/${s.maintenanceAsset.id}`} onClick={e => e.stopPropagation()}
                        className="d-block" style={{ fontSize: 11 }}>
                        {s.maintenanceAsset.name} →
                      </Link>
                    )}
                    <div style={{ fontSize: 26, fontWeight: 700, color: st.color === 'danger' ? '#dc3545' : st.color === 'success' ? '#198754' : '#6c757d', lineHeight: 1.2, marginTop: 4 }}>
                      {fmtTemp(s.lastTempC, unit)}
                    </div>
                    {s.lastHumidity != null && (
                      <div className="text-muted" style={{ fontSize: 11 }}>💧 {s.lastHumidity.toFixed(0)}% RH</div>
                    )}
                    <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
                      {s.lastReadAt ? timeSince(s.lastReadAt) : 'No readings yet'}
                      {s.minTempC != null || s.maxTempC != null
                        ? ` · Range: ${s.minTempC != null ? fmtTemp(s.minTempC, unit) : '—'} – ${s.maxTempC != null ? fmtTemp(s.maxTempC, unit) : '—'}`
                        : ''
                      }
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Sensor peek bar (fixed, appears when cards scroll off screen) ── */}
      {sensorsHidden && activeSensors.length > 0 && (
        <div style={{ position: 'fixed', top: 'var(--topbar-height)', left: 0, right: 0, zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
          {/* Pill trigger */}
          <div
            style={{ pointerEvents: 'all', background: '#fff', borderRadius: '0 0 12px 12px', boxShadow: '0 2px 10px rgba(0,0,0,0.15)', padding: '5px 18px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setPeekOpen(o => !o)}
          >
            {activeSensors.map(s => {
              const st = sensorStatus(s);
              const dot = st.color === 'danger' ? '#dc3545' : st.color === 'success' ? '#198754' : '#6c757d';
              return <span key={s.id} title={`${s.name}: ${fmtTemp(s.lastTempC, s.displayUnit || 'F')}`} style={{ width: 9, height: 9, borderRadius: '50%', background: dot, display: 'inline-block', flexShrink: 0 }} />;
            })}
            <span style={{ fontSize: 11, color: '#6c757d', fontWeight: 500 }}>Sensors</span>
            <span style={{ fontSize: 11, color: '#6c757d', display: 'inline-block', transition: 'transform 0.2s', transform: peekOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
          </div>

          {/* Expanded sensor mini-grid */}
          {peekOpen && (
            <div style={{ pointerEvents: 'all', background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '10px 16px', width: '100%', maxWidth: 960, borderRadius: '0 0 10px 10px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {activeSensors.map(s => {
                  const st   = sensorStatus(s);
                  const unit = s.displayUnit || 'F';
                  const col  = st.color === 'danger' ? '#dc3545' : st.color === 'success' ? '#198754' : '#6c757d';
                  return (
                    <div
                      key={s.id}
                      onClick={() => { setChartSensor(s); setPeekOpen(false); }}
                      style={{ border: `1.5px solid ${col}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', minWidth: 110, background: '#fafafa' }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>{s.name}</div>
                      {s.locationName && <div style={{ fontSize: 10, color: '#888' }}>{s.locationName}</div>}
                      <div style={{ fontSize: 18, fontWeight: 700, color: col, lineHeight: 1.2 }}>{fmtTemp(s.lastTempC, unit)}</div>
                      <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>{timeSince(s.lastReadAt) || 'No data'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Readings table ────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <h6 className="mb-0 d-flex align-items-center gap-2">
          Reading History
          {refreshing && <span className="spinner-border spinner-border-sm text-secondary" style={{ width: 12, height: 12, borderWidth: 2 }} />}
        </h6>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <select className="form-select form-select-sm" style={{ width: 'auto' }} value={filterSensor} onChange={e => { setFilterSensor(e.target.value); setPage(1); }}>
            <option value="">All sensors</option>
            {sensors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={filterStart} onChange={e => { setFilterStart(e.target.value); setPage(1); }} />
          <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={filterEnd} onChange={e => { setFilterEnd(e.target.value); setPage(1); }} />
          <div className="form-check mb-0">
            <input className="form-check-input" type="checkbox" id="flaggedOnly" checked={filterFlagged}
              onChange={e => { setFilterFlagged(e.target.checked); setPage(1); }} />
            <label className="form-check-label" htmlFor="flaggedOnly" style={{ fontSize: 12 }}>Flagged only</label>
          </div>
          {(filterSensor || filterStart || filterEnd || filterFlagged) && (
            <button className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }} onClick={() => { setFilterSensor(''); setFilterStart(''); setFilterEnd(''); setFilterFlagged(false); setPage(1); }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted py-4">
          <div className="spinner-border spinner-border-sm me-2" /> Loading…
        </div>
      ) : readingsData.readings.length === 0 ? (
        <div className="text-center text-muted py-4 border rounded" style={{ fontSize: 13 }}>No readings found.</div>
      ) : (
        <>
          <div>
            <table className="table table-sm table-hover mb-2">
              <thead style={{ position: 'sticky', top: 'var(--topbar-height)', zIndex: 3 }}>
                <tr>
                  {['Time','Sensor','Temp','Humidity','Source','Status','Acknowledged',''].map((h, i) => (
                    <th key={i} style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', paddingTop: 20, verticalAlign: 'bottom' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {readingsData.readings.map(r => {
                  const sensor = r.Sensor;
                  const unit   = sensor?.displayUnit || 'F';
                  const oor    = r.isOutOfRange;
                  const flagged = r.isFlagged;
                  return (
                    <tr key={r.id} className={flagged && !r.acknowledgedAt ? 'table-danger' : oor ? 'table-warning' : ''}>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {new Date(r.recordedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        <div className="fw-semibold">{sensor?.name || '—'}</div>
                        {sensor?.locationName && <div className="text-muted" style={{ fontSize: 10 }}>{sensor.locationName}</div>}
                      </td>
                      <td style={{ fontSize: 13, fontWeight: oor ? 700 : 400 }}>
                        {fmtTemp(r.tempC, unit)}
                      </td>
                      <td style={{ fontSize: 12 }}>{r.humidity != null ? `${r.humidity.toFixed(0)}%` : '—'}</td>
                      <td><span className="badge bg-secondary" style={{ fontSize: 10 }}>{r.source}</span></td>
                      <td>
                        {flagged
                          ? <span className="badge bg-danger" style={{ fontSize: 10 }}>⚑ Flagged ({r.consecutiveCount}×)</span>
                          : oor
                          ? <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>Out of range</span>
                          : <span className="badge bg-success" style={{ fontSize: 10 }}>In range</span>
                        }
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {r.acknowledgedAt
                          ? <span title={r.outOfRangeReason || ''} className={r.autoAcknowledged ? 'text-muted' : 'text-success'}>
                              {r.autoAcknowledged ? '🔧 Auto' : `✓ ${r.AcknowledgedBy?.firstName || r.AcknowledgedBy?.username || 'Acknowledged'}`}
                              {r.outOfRangeReason && <span className="text-muted"> — {r.outOfRangeReason.slice(0, 40)}{r.outOfRangeReason.length > 40 ? '…' : ''}</span>}
                            </span>
                          : '—'
                        }
                      </td>
                      <td>
                        {flagged && !r.acknowledgedAt && (
                          <button className="btn btn-danger btn-sm" style={{ fontSize: 11 }} onClick={() => setAckReading(r)}>
                            Acknowledge
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {readingsData.pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-2">
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span className="align-self-center" style={{ fontSize: 13 }}>Page {page} of {readingsData.pages}</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= readingsData.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {chartSensor && (
        <SensorChartModal
          sensor={chartSensor}
          onClose={() => setChartSensor(null)}
        />
      )}
      {ackReading && (
        <AcknowledgeModal
          reading={ackReading}
          onClose={() => setAckReading(null)}
          onSaved={() => { setAckReading(null); flash('Acknowledged.'); load(true); }}
        />
      )}
      {showManual && (
        <ManualEntryModal
          sensors={sensors}
          onClose={() => setShowManual(false)}
          onSaved={() => { setShowManual(false); flash('Reading saved.'); load(true); }}
        />
      )}
    </div>
    </>
  );
}
